<template lang="pug">
span.root
  slot(
    name="default",
    :executeSearch="executeSearch",
    :filters="filters",
    :setFilterValue="setFilterValue",
    :clearAllFilters="clearAllFilters",
    :extend="extend",
    :extended="extended"
  )
    .search-filters
      slot(
        name="mobileDrawerToggle",
        :toggleMobileDrawer="toggleMobileDrawer",
        :mobileDrawerOpen="mobileDrawerOpen"
      )
        button.search-filters__toggle(
          @click="toggleMobileDrawer",
          :disabled="filters.length ? false : 'disabled'",          
          :tabindex="filters.length ? false : '-1'") {{ config.filterDrawerToggleText }}
      .search-filters__inner(
        :class="{ active: mobileDrawerOpen }",
        @click.self="toggleMobileDrawer(false)"
      )
        slot(
          name="mobileDrawerClose",
          :toggleMobileDrawer="toggleMobileDrawer",
          :mobileDrawerOpen="mobileDrawerOpen"
        )
          button.search-filters__close(@click="toggleMobileDrawer(false)") 
            span {{ config.filterDrawerCloseText }}
        .search-filters__content
          .search-filters__form
            template(v-for="group in filters")
              slot(
                name="group",
                :group="group",
                :initiallyExpanded="config.filterInitiallyExpanded",
                :showZeroResultFilters="config.showZeroResultFilters",
                :setFilterValue="setFilterValue",
                :executeSearch="executeSearch"
                :extend="extend",
                :extended="extended"
              )
                Group(
                  :group="group",
                  :initiallyExpanded="config.filterInitiallyExpanded",
                  :setFilterValue="setFilterValue"
                  :executeSearch="executeSearch"
                  :showZeroResultFilters="config.showZeroResultFilters"
                )
                  template(v-for="filter in group.filters")
                    slot(
                      name="item",
                      :filter="filter",
                      :setFilterValue="setFilterValue"
                      :executeSearch="executeSearch"
                      :extend="extend",
                      :extended="extended"
                      :showZeroResultFilters="config.showZeroResultFilters")
                      Item(:filter="filter", :setFilterValue="setFilterValue" :executeSearch="executeSearch" :showZeroResultFilters="config.showZeroResultFilters")
            slot(
              v-if="!config.filterAutoSubmit"
              name="submit",
              :toggleMobileDrawer="toggleMobileDrawer",
              :filters="filters",
              :filterSubmitText="config.filterSubmitText"
            )
              button.search-filters__btn(
                @click="executeSearch(); toggleMobileDrawer(false)", 
                :disabled="filters.length ? false : 'disabled'",
                :tabindex="filters.length ? false : '-1'"
              ) {{ config.filterSubmitText }}
</template>

<script>
import { mapGetters } from "vuex";
import Group from "./Group.vue";
import Item from "./Item.vue";
import { debounce } from "debounce";

export default {
  name: "BlueFilters",
  components: { Group, Item }, data() {
    return {
      mobileDrawerOpen: false,
    };
  },
  computed: {
		...mapGetters([
			"siteName"
		]),
    filters() {
      if (this.config.showZeroResultFilters) {
        return this.$store.getters.filters;
      }
      return this.$store.getters.filters.filter((g) =>
        g.visible ? true : g.filters.some((f) => f.selected)
      );
    },
    extended() {
      return this.$store.getters.extended;
    },
  },
  methods: {
    clearAllFilters() {
      this.$store.dispatch("removeAllTags");
    },
    toggleMobileDrawer(toggle = !this.mobileDrawerOpen) {
      this.mobileDrawerOpen = toggle;
			const header = document.querySelector("header.header");
			if (!!header && this.siteName == "ORG") {
				if (toggle) {
					header.style.zIndex= "1";
				} else {
					header.style.zIndex= "";
				}
			}
    },
    setFilterValue(filter, val, callback) {
      let value;
      if (typeof val === "object") value = val.target.value;
      else value = val;
      this.$store.commit("setFilterValue", { filter, value });
      if (this.config.filterAutoSubmit) return this.executeSearch(callback);
    },
    extend(callback) {
      this.$store.commit("extend", callback);
    },
		executeSearch: debounce(function (callback) {
				if (this.config.preserveResults) this.$store.commit("clearPreviousItemResults");
				return this.$store.dispatch("executeSearch", callback);
			}, 50)
  },
};
</script>

<style lang="scss" scoped>
span.root {
  display: contents;
}
.search-filters {
  display: flex;
  flex-direction: column;
  padding: 0;
  margin: 0;
  flex: 1 1 100%;
  position: sticky;
  top: 0;
  z-index: 2;

  &__inner {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: 300px;
    transition: transform 0.25s ease;
    transform: translateX(100%);
    z-index: 100;

    &::before,
    &::after {
      content: "";
      top: 0;
      right: 0;
      bottom: 0;
    }

    &::before {
      position: absolute;
      z-index: -1;
      left: 0;
      background: $white;
    }

    &::after {
      position: fixed;
      z-index: -2;
      width: calc(100vw + 300px);
      height: 100vh;
      cursor: pointer;
      background: rgba($black, 0.85);
      transition: 0.25s ease;
      pointer-events: none;
      visibility: hidden;
      opacity: 0;
      transform: translateX(-300px);
    }

    &.active {
      transform: translateX(0);

      &::after {
        visibility: visible;
        opacity: 1;
        pointer-events: all;
        transform: translateX(0);
      }
    }

    @include mq(lg) {
      position: relative;
      height: auto;
      width: auto;
      transform: none;
      transition: none;
      z-index: 0;
      box-shadow: none;
      &::before,
      &::after {
        display: none;
      }
    }

    > *:last-child {
      margin-bottom: 0;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    height: 100%;
    width: 100%;
    padding: 0 1.5rem 1.5rem;

    @include mq(lg) {
      padding: 0;
      height: auto;
      overflow: visible;
    }
  }

  &__form {
    display: flex;
    flex-direction: column;
    padding: 0;
    margin: 0;
  }

  &__toggle,
  &__close,
  &__btn {
    @include clean(button);
    padding: 1rem;
    background: var(--button-background);
    color: var(--button-color);
    transition: var(--button-transition);
    &:hover {
      background: var(--button-hover-background);
      color: var(--button-hover-color);
    }

    &:disabled {
      pointer-events: none;
      opacity: 0.75;
    }
  }

  &__close {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    justify-content: flex-end;
    margin-bottom: -1px;
    padding: 1rem 1.5rem;
    line-height: 1;
    font-size: 0.875rem;
    z-index: 1;
    &::after {
      content: "\d7";
      flex: 0 0 auto;
      font-size: 1.25rem;
      line-height: 0;
    }
  }

  &__toggle,
  &__close {
    @include mq(lg) {
      display: none;
    }
  }
}
</style>